/**
 * Fetch a Tharstern product type's derived EstRequest schema.
 *
 * Headless — returns data, renders nothing. Shared by the catalogue editor
 * (which needs it for an existing item's product type) and the item creator
 * (which needs it the moment a curator picks one), so the two cannot drift
 * into disagreeing about what a product type offers.
 *
 * `schema` is `undefined` while loading, `null` when the product type has no
 * cached schema, and the response once loaded. Those three are deliberately
 * distinct: "still loading" and "nothing cached" mean different things to a
 * curator, and collapsing them is how a screen ends up quietly claiming a
 * product type has no fields.
 */

import { useEffect, useState } from "react";

import { useDataAdapter } from "../data/DataAdapterContext";
import type * as API from "../data/api-types";

export interface EstRequestSchemaState {
  schema: API.DerivedSchemaResponse | null | undefined;
  /** The fetch itself failed — distinct from "no schema cached". */
  failed: boolean;
}

export function useEstRequestSchema(
  productTypeId: number | null,
): EstRequestSchemaState {
  const adapter = useDataAdapter();
  const [schema, setSchema] = useState<
    API.DerivedSchemaResponse | null | undefined
  >(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (productTypeId == null) {
      setSchema(undefined);
      setFailed(false);
      return;
    }
    let cancelled = false;
    setFailed(false);
    setSchema(undefined);
    adapter
      .getEstRequestSchema(productTypeId)
      .then((s) => {
        if (!cancelled) setSchema(s);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, productTypeId]);

  return { schema, failed };
}
